import { DeleteRounded, Event } from '@mui/icons-material'
import { Card, CardContent, IconButton, Button, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { EdgeLabelRenderer, getBezierPath, useReactFlow } from 'reactflow'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../../../generic/systemValues/systemValueLabel'
import { useTheme } from '@emotion/react'
import { SmartSelect } from '../../../generic/smartSelect'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import EventTable from './EventTable'
import { JOB_HIGHLIGHT_COLOR } from '../bomTree'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import LoopIcon from '@mui/icons-material/Loop'
import SettingsIcon from '@mui/icons-material/Settings'

/**
 * @typedef {import('../../../generic/utilities/objectStructures/workflow/workflow').EdgeData} EdgeData EdgeData object
 */

/**
 * S69/S70 (historia 152, RN-02.6/RN-02.7): the color of an edge's stroke. The BOM assembly
 * highlight paints the connections between the highlighted operations in the SAME blue the node
 * border uses for the chip highlight of #28 (`JOB_HIGHLIGHT_COLOR`), so both ends of one action
 * read as one. The edge's own SELECTION still rules: a selected edge keeps its result color, and
 * the highlight only claims the resting one.
 * @param {EdgeData} data
 * @param {Object} theme
 * @returns {string}
 */
// P19 (story 204): WAIT reads with the key the side panel already uses for the same word.
const EVENT_TYPE_KEYS = { WAIT: 'Wait' }

const resolveEdgeStrokeColor = (data = {}, theme) => {
  if (data.selected) {
    switch (data.type) {
      case 'OK': return theme.palette.success.main
      case 'FAIL': return theme.palette.error.main
      default: return theme.palette.grey['50']
    }
  }

  if (data.jobHighlighted) {
    return JOB_HIGHLIGHT_COLOR
  }

  switch (data.type) {
    case 'OK': return theme.palette.success.dark
    case 'FAIL': return theme.palette.error.dark
    default: return theme.palette.grey['600']
  }
}

/**
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {number} props.sourceX
 * @param {number} props.sourceY
 * @param {number} props.targetX
 * @param {number} props.targetY
 * @param {string} props.sourcePosition
 * @param {string} props.targetPosition
 * @param {EdgeData} props.data
 * @param {string} props.markerEnd
 * @param {string} props.source
 * @param {string} props.target
 * @param {boolean} props.editing
 * @returns
 */
export default function WorkflowEdge ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data = {},
  markerEnd,
  source,
  target,
  editing = false
}) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  })

  const [openEventDialog, setOpenEventDialog] = useState(false)
  const [openEmptyDataDialog, setOpenEmptyDataDialog] = useState(false)
  const [prevSelected, setPrevSelected] = useState(data.selected)

  const { t_ } = useSmartTranslation()
  const strokeWidth = 5
  const theme = useTheme()
  const eventTableRef = React.useRef(null)
  const { getEdges, setEdges } = useReactFlow()

  const getStyle = () => {
    const style = { strokeWidth, stroke: resolveEdgeStrokeColor(data, theme) }

    if (data.eventType === 'WAIT') {
      style.strokeDasharray = '6 6'
      return style
    }

    if (data.eventType === 'PERIODIC') {
      style.strokeDasharray = '6 6'
      return style
    }

    return style
  }

  const saveEvent = (crontab, startDate) => {
    data.eventValue = null
    data.startDate = null
    if (crontab) {
      data.eventValue = crontab
      if (crontab.startsWith('FREQ')) {
        data.startDate = startDate
      }
    }
    const updateEdge = getEdges().map((edge) => {
      if (edge.id === id) {
        edge.data = { ...edge.data }
        edge.data.eventValue = data.eventValue
        edge.data.startDate = data.startDate
      }
      data.updateEdge(edge)
      return edge
    })
    setEdges(updateEdge)
  }

  const onConnectivityChange = (newValue) => {
    const newEdgeValues = getEdges().map((edge) => {
      if (edge.id === id) {
        edge.data = { ...edge.data }
        edge.data.connectivity = newValue
        data.updateEdge(edge)
      }
      return edge
    })

    setEdges(newEdgeValues)
  }

  const onEventTypeChange = (newValue) => {
    const newEdgeValues = getEdges().map((edge) => {
      if (edge.id === id) {
        edge.data = { ...edge.data }
        edge.data.eventType = newValue
        edge.data.eventValue = null
        edge.data.startDate = null
        data.updateEdge(edge)
      }
      return edge
    })
    setEdges(newEdgeValues)
  }

  useEffect(() => {
    if (prevSelected && !data.selected) {
      if (data.eventType && !data.eventValue) {
        setOpenEmptyDataDialog(true)
      }
    }
    setPrevSelected(data.selected)
  }, [data.selected, data.eventType, data.eventValue, prevSelected])

  return (
    <>
      <SmartDialog
        setOpen={openEventDialog}
        title={createDialogTitle({ icon: Event, title: t_('Select event') })}
        accept
        close={false}
        acceptCallback={() => {
          if (eventTableRef?.current) {
            if (eventTableRef.current.onSave()) setOpenEventDialog(false)
          }
        }}
        cancelCallback={() => {
          setOpenEventDialog(false)
        }}
        paperWidth='auto'
        renderComponent={
          <EventTable
            ref={eventTableRef}
            crontab={data?.eventValue}
            startDate={data?.startDate}
            type={data.eventType}
            onChange={(crontab, startDate) =>
              saveEvent(crontab, startDate)}
          />
        }
        renderCustomButtons={
          <Button
            color='primary'
            onClick={() => {
              eventTableRef.current?.resetTable()
            }}
            variant='outlined'
          >
            <Typography color='primary'> {t_('CLEAR')} </Typography>
          </Button>
        }
      />
      <SmartDialog
        setOpen={openEmptyDataDialog}
        title={createDialogTitle({ icon: Event, title: t_('Event type is not configured'), color: 'warning' })}
        accept={false}
        cancelCallback={() => {
          setOpenEmptyDataDialog(false)
        }}
        renderComponent={
          <Typography>
            {t_('Event type was changed but no data was saved. Please configure the event before deselecting.')}
          </Typography>
        }
        renderCustomButtons={
          <>
            <Button
              color='error'
              onClick={() => {
                const newEdgeValues = getEdges().map((edge) => {
                  if (edge.id === id) {
                    edge.data = { ...edge.data }
                    edge.data.eventType = null
                    data.updateEdge(edge)
                  }
                  return edge
                })
                setEdges(newEdgeValues)
                setOpenEmptyDataDialog(false)
              }}
              variant='outlined'
              startIcon={<DeleteRounded />}
            >
              <Typography color='error'> {t_('DELETE EVENT')} </Typography>
            </Button>
            <Button
              onClick={() => {
                setOpenEmptyDataDialog(false)
                setOpenEventDialog(true)
              }}
              variant='outlined'
              startIcon={<SettingsIcon />}
            >
              <Typography> {t_('Configure')} </Typography>
            </Button>
          </>
        }
      />
      <path
        id={id}
        style={getStyle()}
        className='react-flow__edge-path'
        d={edgePath}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'none',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {data.eventType === 'WAIT' && (
            <AccessTimeIcon fontSize='small' sx={{ color: 'inherit', opacity: 0.9 }} />
          )}

          {data.eventType === 'PERIODIC' && data.startDate && (
            <CalendarMonthIcon fontSize='small' sx={{ color: 'inherit', opacity: 0.9 }} />
          )}

          {data.eventType === 'PERIODIC' && !data.startDate && (
            <LoopIcon fontSize='small' sx={{ color: 'inherit', opacity: 0.9 }} />
          )}
        </div>
      </EdgeLabelRenderer>
      {!data.disableEdges
        ? (
          <EdgeLabelRenderer>
            <div sx={{ display: 'flex', direction: 'column' }}>
              <Card
                hidden={!data.selected}
                sx={{
                  backgroundColor: theme.palette.background.default,
                  color: 'white',
                  transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                  zIndex: '100000000',
                  width: source !== 'start' ? '11rem' : '10rem',
                  height: source !== 'start' ? '7.5rem' : '3rem',
                  position: 'absolute'
                }}
              >
                <CardContent
                  sx={{
                    scale: '70%',
                    zIndex: '100000',
                    padding: '0'
                  }}
                  style={{ pointerEvents: 'all' }}
                >
                  {data.connectivity && source !== 'start'
                    ? <SmartSelect
                        label={t_('Connectivity')}
                        value={data.connectivity}
                        selectableOptions={['FLEXIBLE', 'MANDATORY']}
                        optionLabelTransform={(connectivity) => systemValueCodeLabel(t_, 'ResultConnectivityEnum', connectivity)}
                        size='small'
                        width='10rem'
                        disabled={!editing}
                        onChange={(value) => { onConnectivityChange(value) }}
                      />
                    : null}
                  {source !== 'start'
                    ? (
                      <div style={{ display: 'flex', marginTop: '1rem' }}>
                        <SmartSelect
                          disabled={!editing}
                          label={t_('When')}
                          value={data.eventType}
                          selectableOptions={['PERIODIC', 'WAIT']}
                          optionLabelTransform={(eventType) => systemValueCodeLabel(t_, 'ResultEventTypeEnum', eventType, EVENT_TYPE_KEYS)}
                          size='small'
                          width='10rem'
                          disableClearable={false}
                          onChange={(value) => { onEventTypeChange(value) }}
                        />
                        <IconButton
                          disabled={!editing || !data.eventType}
                          className='nodrag'
                          sx={{ marginLeft: '0.5rem' }}
                          onClick={() => setOpenEventDialog(true)}
                        >
                          <Event />
                        </IconButton>
                      </div>
                      )
                    : null}
                  <IconButton
                    disabled={!editing}
                    color='error'
                    sx={{ transform: 'translate(150%, 0%)' }}
                    className='nodrag'
                    onClick={() => {
                      const edge = getEdges().find(e => e.id === id)
                      data.deleteEdge(edge)
                      setEdges((es) => es.filter((e) => e.id !== id))
                    }}
                  >
                    <DeleteRounded />
                  </IconButton>
                </CardContent>
              </Card>
            </div>
          </EdgeLabelRenderer>
          )
        : null}
    </>
  )
}

export { resolveEdgeStrokeColor }
